import { useState, useEffect, lazy, Suspense } from 'react';
import { LangProvider } from '../../hooks/useLang';
import { useInView } from '../../hooks/useInView';
import { useLearningProgress } from '../../hooks/useLearningProgress';
import SEOHead from '../../components/feature/SEOHead';
import Navbar from './components/Navbar';
import WarningBar from './components/WarningBar';
import RiskModal from './components/RiskModal';
import Hero from './components/Hero';
import SectionPreview from './components/SectionPreview';
import NewsTicker from './components/NewsTicker';
import ReadProgressWidget from './components/ReadProgressWidget';
import BackToTop from './components/BackToTop';
import LearningProgressButton from './components/LearningProgressButton';

// ── Lazy-loaded chunks (code-split by route/section) ──
const RWAFutureVision    = lazy(() => import('./components/RWAFutureVision'));
const RWATimeline        = lazy(() => import('./components/RWATimeline'));
const SiteMap            = lazy(() => import('./components/SiteMap'));
const RWAEntry           = lazy(() => import('./components/RWAEntry'));
const PolicySection      = lazy(() => import('./components/PolicySection'));
const CaseLibrary        = lazy(() => import('./components/CaseLibrary'));
const HKInstitutions     = lazy(() => import('./components/HKInstitutions'));
const LandingProcess     = lazy(() => import('./components/LandingProcess'));
const RWANewsFeed        = lazy(() => import('./components/RWANewsFeed'));
const EducationNews      = lazy(() => import('./components/EducationNews'));
const Glossary           = lazy(() => import('./components/Glossary'));
const BlockchainTechSection = lazy(() => import('./components/BlockchainTechSection'));
const ExpertProfiles     = lazy(() => import('./components/ExpertProfiles'));
const ColumnBanner       = lazy(() => import('./components/ColumnBanner'));
const RWABookshelf       = lazy(() => import('./components/RWABookshelf'));
const PlatformDirectory  = lazy(() => import('./components/PlatformDirectory'));
const TrainingCoursesBanner = lazy(() => import('./components/TrainingCoursesBanner'));
const AboutSection       = lazy(() => import('./components/AboutSection'));
const Footer             = lazy(() => import('./components/Footer'));
const FloatingWidget     = lazy(() => import('./components/FloatingWidget'));

// ── Skeleton placeholder ──
function SectionSkeleton({ height = 'h-48' }: { height?: string }) {
  return (
    <div
      className={`w-full ${height} bg-gradient-to-r from-gray-100 via-gray-50 to-gray-100 animate-pulse`}
      aria-hidden="true"
    />
  );
}

/**
 * InViewSection — wraps a lazy section with Intersection Observer.
 * Supports directional entrance animations: left / right / up (default).
 */
interface InViewSectionProps {
  children: React.ReactNode;
  skeletonHeight?: string;
  rootMargin?: string;
  id?: string;
  animDelay?: number;
  /** Animation direction: 'left' | 'right' | 'up' */
  direction?: 'left' | 'right' | 'up';
  /** data-section-id for read-progress tracking */
  sectionId?: string;
}

function getInitialTransform(direction: 'left' | 'right' | 'up'): string {
  switch (direction) {
    case 'left': return 'translateX(-48px)';
    case 'right': return 'translateX(48px)';
    case 'up':
    default: return 'translateY(28px)';
  }
}

function InViewSection({
  children,
  skeletonHeight = 'h-64',
  rootMargin = '300px',
  id,
  animDelay = 0,
  direction = 'up',
  sectionId,
}: InViewSectionProps) {
  const [ref, inView] = useInView<HTMLDivElement>({ rootMargin, once: true });
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (inView) {
      const timer = setTimeout(() => setVisible(true), animDelay);
      return () => clearTimeout(timer);
    }
  }, [inView, animDelay]);

  return (
    <div ref={ref} id={id} data-section-id={sectionId}>
      {inView ? (
        <div
          style={{
            opacity: visible ? 1 : 0,
            transform: visible ? 'translate(0,0)' : getInitialTransform(direction),
            transition: `opacity 0.6s cubic-bezier(0.22,1,0.36,1), transform 0.6s cubic-bezier(0.22,1,0.36,1)`,
          }}
        >
          <Suspense fallback={<SectionSkeleton height={skeletonHeight} />}>
            {children}
          </Suspense>
        </div>
      ) : (
        <SectionSkeleton height={skeletonHeight} />
      )}
    </div>
  );
}

function SectionTracker() {
  const { markVisited } = useLearningProgress();

  useEffect(() => {
    const sectionIds = [
      'rwa-entry', 'blockchain', 'policy', 'cases', 'hk-institutions',
      'landing', 'news', 'glossary', 'blockchain-tech', 'platforms',
      'future-vision', 'about',
    ];

    const observers: IntersectionObserver[] = [];

    sectionIds.forEach((id) => {
      // Try both direct id and data-section-id
      const el = document.getElementById(id) || document.querySelector(`[data-section-id="${id}"]`);
      if (!el) return;
      const obs = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              markVisited(id);
            }
          });
        },
        { threshold: 0.2 }
      );
      obs.observe(el);
      observers.push(obs);
    });

    return () => observers.forEach((o) => o.disconnect());
  }, [markVisited]);

  return null;
}

export default function HomePage() {
  const [riskModalOpen, setRiskModalOpen] = useState(false);
  const [isFirstVisit, setIsFirstVisit] = useState(false);

  useEffect(() => {
    const hasVisited = localStorage.getItem('rwa99_compliance_confirmed');
    if (!hasVisited) {
      const timer = setTimeout(() => {
        setIsFirstVisit(true);
        setRiskModalOpen(true);
      }, 500);
      return () => clearTimeout(timer);
    }
  }, []);

  const handleModalClose = () => {
    setRiskModalOpen(false);
    if (isFirstVisit) {
      localStorage.setItem('rwa99_compliance_confirmed', '1');
      setIsFirstVisit(false);
    }
  };

  return (
    <LangProvider>
      <SEOHead />
      <div className="min-h-screen bg-[#FAFAFA] text-gray-800">
        <Navbar onRiskModal={() => setRiskModalOpen(true)} />
        <WarningBar />
        <RiskModal open={riskModalOpen} onClose={handleModalClose} isFirstVisit={isFirstVisit} />

        <main className="pb-12">
          <InViewSection skeletonHeight="h-screen" rootMargin="500px" direction="up" sectionId="rwa-future">
            <RWAFutureVision />
          </InViewSection>

          <Hero />
          <NewsTicker />
          <SectionPreview />

          <InViewSection skeletonHeight="h-[600px]" rootMargin="300px" direction="left" sectionId="rwa-timeline">
            <RWATimeline />
          </InViewSection>

          <InViewSection skeletonHeight="h-64" rootMargin="300px" direction="right" sectionId="site-map">
            <SiteMap />
          </InViewSection>

          <InViewSection skeletonHeight="h-[700px]" rootMargin="300px" direction="left" sectionId="rwa-entry">
            <RWAEntry />
          </InViewSection>

          <InViewSection skeletonHeight="h-[600px]" rootMargin="300px" direction="right" sectionId="policy">
            <PolicySection />
          </InViewSection>

          <InViewSection skeletonHeight="h-64" rootMargin="300px" direction="left" sectionId="cases">
            <CaseLibrary />
          </InViewSection>

          <InViewSection skeletonHeight="h-[700px]" rootMargin="300px" direction="right" sectionId="hk-institutions">
            <HKInstitutions />
          </InViewSection>

          <InViewSection skeletonHeight="h-[600px]" rootMargin="300px" direction="left" sectionId="landing">
            <LandingProcess />
          </InViewSection>

          <InViewSection skeletonHeight="h-64" rootMargin="300px" direction="right" sectionId="news">
            <RWANewsFeed />
          </InViewSection>

          <InViewSection skeletonHeight="h-64" rootMargin="300px" direction="left">
            <EducationNews />
          </InViewSection>

          <InViewSection skeletonHeight="h-[700px]" rootMargin="300px" direction="right" sectionId="glossary">
            <Glossary />
          </InViewSection>

          <InViewSection skeletonHeight="h-64" rootMargin="300px" direction="left" sectionId="blockchain">
            <BlockchainTechSection />
          </InViewSection>

          <InViewSection skeletonHeight="h-64" rootMargin="300px" direction="right">
            <ExpertProfiles />
          </InViewSection>

          <InViewSection skeletonHeight="h-48" rootMargin="300px" direction="left">
            <ColumnBanner />
          </InViewSection>

          <InViewSection skeletonHeight="h-64" rootMargin="300px" direction="right">
            <RWABookshelf />
          </InViewSection>

          <InViewSection skeletonHeight="h-64" rootMargin="300px" direction="left">
            <PlatformDirectory />
          </InViewSection>

          <InViewSection skeletonHeight="h-48" rootMargin="300px" direction="right">
            <TrainingCoursesBanner />
          </InViewSection>

          <InViewSection skeletonHeight="h-64" rootMargin="300px" direction="up" sectionId="about">
            <AboutSection />
          </InViewSection>
        </main>

        <InViewSection skeletonHeight="h-48" rootMargin="300px" direction="up">
          <Footer />
        </InViewSection>

        <Suspense fallback={null}>
          <FloatingWidget />
        </Suspense>

        {/* Read Progress Widget */}
        <ReadProgressWidget />
        {/* Back to Top + Page Progress Bar */}
        <BackToTop />
        {/* Learning Progress Button */}
        <LearningProgressButton />
        {/* Section Visit Tracker */}
        <SectionTracker />
      </div>
    </LangProvider>
  );
}
